/**
 * ConfilexLogo — Identité visuelle officielle CONFILEX (refonte 2026-02).
 *
 * CONCEPT
 * -------
 * Un « C » crescent (croissant) embrasse trois points colorés (jaune, vert,
 * bleu) disposés en TRIANGLE et reliés par des hairlines : l'image d'un
 * réseau intelligent qui sélectionne trois spécialistes pour l'utilisateur.
 *
 * SYMBOLIQUE
 *  - Le C ouvert    → l'écoute, l'accueil, la mise en relation
 *  - Les 3 points   → les 3 spécialistes proposés, pluralité, analyse multicritère
 *  - Le triangle    → la sélection, l'équilibre, le tripartite
 *  - L'accent vert  → la lettre X (« croisement », orientation positive)
 *
 * CONTRAINTES DE MARQUE (impératives)
 *  - Les 3 points ne sont JAMAIS supprimés.
 *  - Aucun élément n'évoque la justice, le tribunal, la balance ou la finance.
 *  - Lisible de 16 px (favicon) à 2000 px (signalétique).
 *
 * PALETTE INSTITUTIONNELLE — UNIFIÉE Vert profond + Or
 *  - Vert sapin   #0B3A2A  → confiance, institution, stabilité (ex "navy")
 *  - Vert prairie #3FA66A  → orientation, accompagnement
 *  - Or signature #C9A961  → accent premium, mise en relation
 *  - Or soleil    #F5C400  → 3e point — clarté, réseau intelligent
 *  - Ivoire       #FAF7F1  → fond clair de référence
 *
 * VARIANTES
 *  - `horizontal` : icône + mot CONFILEX + signature (default)
 *  - `vertical`   : icône en haut, mot dessous, signature dessous
 *  - `icon`       : icône seule (favicon, app mobile, avatar réseaux)
 *
 * THÈMES
 *  - `light` (par défaut)  → fond clair (ivoire/blanc)
 *  - `dark`                → fond vert sapin profond
 */

export const BRAND_COLORS = {
  navy: "#0B3A2A",      // alias historique — désormais vert sapin profond
  green: "#3FA66A",
  yellow: "#F5C400",
  blue: "#C9A961",      // alias historique — désormais or signature
  ivory: "#FAF7F1",
};

export const BRAND_TAGLINE = "Trouvez le bon spécialiste, en toute confiance";
export const BRAND_TAGLINE_TAIL = "en toute confiance";  // partie en vert

/**
 * Le symbole pur (« C » crescent + 3 points en triangle + connexions).
 * viewBox 64x64 — toutes les coordonnées en unités logiques.
 */
export function ConfilexMark({
  size = 40,
  theme = "light",
  showConnections = true,
  className = "",
  title = "CONFILEX",
}) {
  const arcColor = theme === "dark" ? "#FFFFFF" : BRAND_COLORS.navy;
  const connColor = theme === "dark" ? "#FFFFFF" : BRAND_COLORS.navy;
  // Position des 3 points en TRIANGLE (jaune en haut, vert et bleu en bas)
  const YELLOW = { x: 38, y: 22 };
  const GREEN = { x: 26, y: 42 };
  const BLUE = { x: 44, y: 42 };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      {/* Le C — crescent ouvert à droite (arc de ~300°, sens anti-horaire) */}
      <path
        d="M 50 16 A 22 22 0 1 0 50 48"
        fill="none"
        stroke={arcColor}
        strokeWidth="5"
        strokeLinecap="round"
      />
      {/* Connexions triangulaires entre les 3 points (réseau) */}
      {showConnections && (
        <g
          stroke={connColor}
          strokeOpacity={theme === "dark" ? 0.6 : 0.35}
          strokeWidth="0.9"
          strokeLinecap="round"
        >
          <line x1={YELLOW.x} y1={YELLOW.y} x2={GREEN.x} y2={GREEN.y} />
          <line x1={GREEN.x} y1={GREEN.y} x2={BLUE.x} y2={BLUE.y} />
          <line x1={BLUE.x} y1={BLUE.y} x2={YELLOW.x} y2={YELLOW.y} />
        </g>
      )}
      {/* Les 3 points colorés — invariants de marque */}
      <circle cx={YELLOW.x} cy={YELLOW.y} r="3.7" fill={BRAND_COLORS.yellow} />
      <circle cx={GREEN.x} cy={GREEN.y} r="3.7" fill={BRAND_COLORS.green} />
      <circle cx={BLUE.x} cy={BLUE.y} r="3.7" fill={BRAND_COLORS.blue} />
    </svg>
  );
}

/**
 * Wordmark "CONFILEX" — sans-serif moderne, lettre X en accent vert.
 * Couleurs via className Tailwind (priorité plus fiable que `style` face
 * aux héritages comme `text-white/70` du Footer).
 */
export function ConfilexWordmark({ size = 22, theme = "light", className = "" }) {
  const baseColor = theme === "dark" ? "text-white" : "text-[#0B3A2A]";
  return (
    <span
      className={`inline-block font-sans font-extrabold tracking-tight leading-none ${baseColor} ${className}`}
      style={{ fontSize: size }}
    >
      CONFILE<span className="text-[#3FA66A]">X</span>
    </span>
  );
}

/**
 * Trio de petits points colorés — souvent placé sous le wordmark pour
 * renforcer l'identité graphique (rappel du symbole en miniature).
 */
export function BrandDots({ size = 6, gap = 6, className = "" }) {
  return (
    <span className={`inline-flex items-center ${className}`} style={{ gap }}>
      <span className="rounded-full" style={{ width: size, height: size, background: BRAND_COLORS.yellow }} />
      <span className="rounded-full" style={{ width: size, height: size, background: BRAND_COLORS.green }} />
      <span className="rounded-full" style={{ width: size, height: size, background: BRAND_COLORS.blue }} />
    </span>
  );
}

/**
 * Logo CONFILEX complet — toutes les déclinaisons officielles.
 */
export default function ConfilexLogo({
  variant = "horizontal",
  theme = "light",
  withTagline = false,
  size = 36,
  className = "",
}) {
  const navy = theme === "dark" ? "text-white" : "text-[#0B3A2A]";
  const tagNavyClass = theme === "dark" ? "text-white" : "text-[#0B3A2A]";

  if (variant === "icon") {
    return <ConfilexMark size={size} theme={theme} className={className} />;
  }

  if (variant === "vertical") {
    return (
      <div className={`inline-flex flex-col items-center gap-3 ${className}`}>
        <ConfilexMark size={size} theme={theme} />
        <ConfilexWordmark size={size * 0.62} theme={theme} />
        {withTagline && (
          <>
            <BrandDots size={size * 0.13} gap={size * 0.18} className="mt-1" />
            <span className={`text-[10px] tracking-[0.2em] uppercase ${tagNavyClass} mt-0.5`}>
              Trouvez le bon expert,{" "}
              <span style={{ color: BRAND_COLORS.green }}>en toute confiance</span>
            </span>
          </>
        )}
      </div>
    );
  }

  // horizontal (default)
  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      <ConfilexMark size={size} theme={theme} />
      <div className="flex flex-col leading-none">
        <ConfilexWordmark size={size * 0.6} theme={theme} />
        {withTagline && (
          <span className={`text-[10px] tracking-wider mt-1.5 ${navy}`}>
            Trouvez le bon expert,{" "}
            <span style={{ color: BRAND_COLORS.green }}>en toute confiance</span>
          </span>
        )}
      </div>
    </div>
  );
}
